import { collection, doc, getDoc, getDocs, query, QuerySnapshot, DocumentData } from 'firebase/firestore';
import { db } from '../config/firebase'; // Assuming firebase config is here
import { PMT2ProblemsData } from '../../functions/src/types/pmt2Types'; // Adjust path as needed

const PROBLEMS_COLLECTION = 'pmt2_problems';
const CA_MICRO_AUTO_ISSUES_COLLECTION = 'pmt2_ca_micro_auto_issues';
const AVG_INVENTORY_LEVELS_COLLECTION = 'pmt2_average_inventory_levels';
const PROFIT_DECLINE_SKUS_COLLECTION = 'pmt2_profit_decline_skus';
const PRODUCTS_COLLECTION = 'pmt2_data'; // Base collection for products
const CLIENT_URL_DB_COLLECTION = 'pmt2_clienturldatabase';
const BROAD_SEEDS_HELPER_COLLECTION = 'pmt2_broad_seeds_helper';


/**
 * Fetches the company-specific document from the pmt2_problems collection.
 */
export const getPmt2ProblemsData = async (companyName: string): Promise<PMT2ProblemsData | null> => {
    if (!companyName) return null;
    try {
        const docRef = doc(db, PROBLEMS_COLLECTION, companyName);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return docSnap.data() as PMT2ProblemsData;
        } else {
            console.warn(`[ProblemsService] No problems document found for company: ${companyName}`);
            return null;
        }
    } catch (error) {
        console.error(`[ProblemsService] Error fetching problems data for ${companyName}:`, error);
        throw error;
    }
};

/**
 * Fetches all entries from a company's subcollection (e.g., entries for issues, inventory, profit decline).
 */
const getCompanySubcollectionEntries = async (
    baseCollection: string,
    companyName: string,
    subcollection = 'entries'
): Promise<DocumentData[]> => {
    if (!companyName) return [];
    try {
        const entriesRef = collection(db, baseCollection, companyName, subcollection);
        const q = query(entriesRef);
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    } catch (error) {
        console.error(`[ProblemsService] Error fetching ${subcollection} for ${companyName} in ${baseCollection}:`, error);
        throw error; // Re-throw
    }
};

/**
 * Fetches all CA/Micro Auto Issues entries for a company.
 */
export const getPmt2CaMicroAutoIssues = (companyName: string): Promise<DocumentData[]> => {
    return getCompanySubcollectionEntries(CA_MICRO_AUTO_ISSUES_COLLECTION, companyName);
};

/**
 * Fetches all Average Inventory Levels entries for a company.
 */
export const getPmt2AverageInventoryLevels = (companyName: string): Promise<DocumentData[]> => {
    return getCompanySubcollectionEntries(AVG_INVENTORY_LEVELS_COLLECTION, companyName);
};

/**
 * Fetches all Profit Decline SKUs entries for a company.
 */
export const getPmt2ProfitDeclineSkus = (companyName: string): Promise<DocumentData[]> => {
    return getCompanySubcollectionEntries(PROFIT_DECLINE_SKUS_COLLECTION, companyName);
};

/**
 * Fetches all product documents for a company from pmt2_data.
 */
export const getPmt2ProductDataForCompany = (companyName: string): Promise<DocumentData[]> => {
     // The subcollection here is 'products', not 'entries'
    return getCompanySubcollectionEntries(PRODUCTS_COLLECTION, companyName, 'products');
};


/**
 * Fetches a single item document from the pmt2_clienturldatabase collection.
 */
export const getClientUrlDatabaseItem = async (
    companyName: string,
    itemName: string
): Promise<DocumentData | null> => {
    if (!companyName || !itemName) return null;
    try {
        const docRef = doc(db, CLIENT_URL_DB_COLLECTION, companyName, 'items', itemName);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        } else {
            console.warn(`[ProblemsService] Client URL DB item '${itemName}' not found for company: ${companyName}`);
            return null;
        }
    } catch (error) {
        console.error(`[ProblemsService] Error fetching Client URL DB item '${itemName}' for ${companyName}:`, error);
        throw error;
    }
};


/**
 * Fetches the Out of Stock stats document for a specific ASIN.
 */
export const getOutOfStockStats = async (
    companyName: string,
    asin: string
): Promise<DocumentData | null> => {
    if (!companyName || !asin) return null;
    try {
        const docRef = doc(db, 'pmt2_out_of_stock_stats', companyName, 'entries', asin);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        } else {
            console.warn(`[ProblemsService] OutOfStockStats document '${asin}' not found for company: ${companyName}`);
            return null;
        }
    } catch (error) {
        console.error(`[ProblemsService] Error fetching OutOfStockStats document '${asin}' for ${companyName}:`, error);
        throw error;
    }
};


/**
 * Fetches the seed strings from a specific column document in the broad seeds helper collection.
 * Assumes seeds are stored as values in a map within the document.
 */
export const getBroadSeedsColumn = async (
    companyName: string,
    columnId: string // e.g., 'ctas_and_broad_seeds', 'unique_words'
): Promise<string[]> => {
    if (!companyName || !columnId) return [];
    try {
        const docRef = doc(db, BROAD_SEEDS_HELPER_COLLECTION, companyName, 'columns', columnId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            const data = docSnap.data();
            if (data) {
                // Extract values from the map, filter out metadata and non-strings
                return Object.entries(data)
                    // Filter out known metadata fields and ensure the key looks like a number (simple check)
                    .filter(([key, value]) => !isNaN(parseInt(key)) && typeof value === 'string')
                    .map(([key, value]) => value as string); // Extract the string value
            }
            return []; // Return empty if data object is empty/null
        } else {
            console.warn(`[ProblemsService] Broad Seeds column '${columnId}' not found for company: ${companyName}`);
            return [];
        }
    } catch (error) {
        console.error(`[ProblemsService] Error fetching Broad Seeds column '${columnId}' for ${companyName}:`, error);
        throw error;
    }
};